import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy } from "react";
import { AuthGuard }  from "./guards/AuthGuard";
import { RoleGuard }  from "./guards/RoleGuard";
import { GuestGuard } from "./guards/GuestGuard";
import { ROUTES }     from "@/lib/constants";

// ─── Layouts ──────────────────────────────────────────────────────────────────
const AuthLayout = lazy(() => import("@/components/layout/AuthLayout").then((m) => ({ default: m.AuthLayout })));
const AppLayout  = lazy(() => import("@/components/layout/AppLayout").then((m) => ({ default: m.AppLayout })));

// ─── Auth pages ───────────────────────────────────────────────────────────────
const LoginPage        = lazy(() => import("@/features/auth/pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const RegisterPage     = lazy(() => import("@/features/auth/pages/RegisterPage").then((m) => ({ default: m.RegisterPage })));
const ForgotPage       = lazy(() => import("@/features/auth/pages/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })));

// ─── Dashboard ────────────────────────────────────────────────────────────────
const DashboardPage    = lazy(() => import("@/features/dashboard/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })));

// ─── Content ──────────────────────────────────────────────────────────────────
const ContentPage       = lazy(() => import("@/features/content/pages/ContentPage").then((m) => ({ default: m.ContentPage })));
const ContentDetailPage = lazy(() => import("@/features/content/pages/ContentDetailPage").then((m) => ({ default: m.ContentDetailPage })));

// ─── Playground ───────────────────────────────────────────────────────────────
const PlaygroundPage         = lazy(() => import("@/features/playground/pages/PlaygroundPage").then((m) => ({ default: m.PlaygroundPage })));
const PlaygroundOopPage      = lazy(() => import("@/features/playground/pages/PlaygroundOopPage").then((m) => ({ default: m.PlaygroundOopPage })));
const PlaygroundSolidPage    = lazy(() => import("@/features/playground/pages/PlaygroundSolidPage").then((m) => ({ default: m.PlaygroundSolidPage })));
const PlaygroundPatternsPage = lazy(() => import("@/features/playground/pages/PlaygroundPatternsPage").then((m) => ({ default: m.PlaygroundPatternsPage })));

// ─── SDLC ─────────────────────────────────────────────────────────────────────
const SrsPage = lazy(() => import("@/features/sdlc/pages/SrsPage").then((m) => ({ default: m.SrsPage })));
const UmlPage = lazy(() => import("@/features/sdlc/pages/UmlPage").then((m) => ({ default: m.UmlPage })));
const WbsPage = lazy(() => import("@/features/sdlc/pages/WbsPage").then((m) => ({ default: m.WbsPage })));

// ─── Chat ─────────────────────────────────────────────────────────────────────
const ChatPage = lazy(() => import("@/features/chat/pages/ChatPage").then((m) => ({ default: m.ChatPage })));

// ─── Profile ──────────────────────────────────────────────────────────────────
const ProfilePage = lazy(() => import("@/features/profile/pages/ProfilePage").then((m) => ({ default: m.ProfilePage })));

// ─── Admin ────────────────────────────────────────────────────────────────────
const AdminDashboardPage = lazy(() => import("@/features/admin/pages/AdminDashboardPage").then((m) => ({ default: m.AdminDashboardPage })));
const AdminUsersPage     = lazy(() => import("@/features/admin/pages/AdminUsersPage").then((m) => ({ default: m.AdminUsersPage })));
const AdminContentPage   = lazy(() => import("@/features/admin/pages/AdminContentPage").then((m) => ({ default: m.AdminContentPage })));
const AdminFeaturesPage  = lazy(() => import("@/features/admin/pages/AdminFeaturesPage").then((m) => ({ default: m.AdminFeaturesPage })));

// ─── 404 ──────────────────────────────────────────────────────────────────────
const NotFoundPage = lazy(() => import("@/components/shared/NotFound").then((m) => ({ default: m.NotFound })));

// ─── Router ───────────────────────────────────────────────────────────────────
export const router = createBrowserRouter([
  { path: ROUTES.HOME, element: <Navigate to={ROUTES.DASHBOARD} replace /> },

  // Guest-only (redirects away if already logged in)
  {
    element: <GuestGuard />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: ROUTES.LOGIN,    element: <LoginPage /> },
          { path: ROUTES.REGISTER, element: <RegisterPage /> },
          { path: ROUTES.FORGOT,   element: <ForgotPage /> },
        ],
      },
    ],
  },

  // Protected (requires auth)
  {
    element: <AuthGuard />,
    children: [
      {
        element: <AppLayout />,
        children: [
          // Home
          { path: ROUTES.DASHBOARD, element: <DashboardPage /> },

          // Profile
          { path: ROUTES.PROFILE, element: <ProfilePage /> },

          // Content
          { path: ROUTES.CONTENT,        element: <ContentPage /> },
          { path: ROUTES.CONTENT_DETAIL, element: <ContentDetailPage /> },

          // Playground
          { path: ROUTES.PLAYGROUND,          element: <PlaygroundPage /> },
          { path: ROUTES.PLAYGROUND_OOP,      element: <PlaygroundOopPage /> },
          { path: ROUTES.PLAYGROUND_SOLID,    element: <PlaygroundSolidPage /> },
          { path: ROUTES.PLAYGROUND_PATTERNS, element: <PlaygroundPatternsPage /> },

          // SDLC tools
          { path: ROUTES.SRS, element: <SrsPage /> },
          { path: ROUTES.UML, element: <UmlPage /> },
          { path: ROUTES.WBS, element: <WbsPage /> },

          // Chat
          { path: ROUTES.CHAT, element: <ChatPage /> },

          // Admin-only
          {
            element: <RoleGuard allowedRoles={["SuperAdmin"]} />,
            children: [
              { path: ROUTES.ADMIN,          element: <AdminDashboardPage /> },
              { path: ROUTES.ADMIN_USERS,    element: <AdminUsersPage /> },
              { path: ROUTES.ADMIN_CONTENT,  element: <AdminContentPage /> },
              { path: ROUTES.ADMIN_FEATURES, element: <AdminFeaturesPage /> },
            ],
          },
        ],
      },
    ],
  },

  { path: "*", element: <NotFoundPage /> },
]);
